/**
 * Create or edit one emotive tag. Creators write it in a script as [tag]; a bracketed word that
 * is not an active tag is refused on generate. Mount only while open (the page keys it per tag).
 */

import { useMemo, useState, type ChangeEvent } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { narrationApi } from '@api/endpoints/narration'
import type { AdminNarrationTagCategoryDto, AdminNarrationTagDto } from '@api/types/narration'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import {
  type TagDraft,
  blankTagDraft,
  narrationErrorMessage,
  narrationTagsKey,
  normalizeTagToken,
  tagDraftFromDto,
  tagRequestFromDraft,
  validateTagDraft,
} from './narrationForm'

export function NarrationTagDialog({
  tag,
  nextSortOrder,
  existing,
  categories,
  defaultCategoryId = '',
  onClose,
}: {
  tag: AdminNarrationTagDto | null
  nextSortOrder: number
  existing: readonly AdminNarrationTagDto[]
  categories: readonly AdminNarrationTagCategoryDto[]
  /** A new tag starts in this category (the table's filter), '' = none. */
  defaultCategoryId?: string
  onClose: () => void
}) {
  const creating = tag === null
  const dialogProps = useMobileDialogProps()
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<TagDraft>(() => (tag ? tagDraftFromDto(tag) : blankTagDraft(nextSortOrder, defaultCategoryId)))
  const [attempted, setAttempted] = useState(false)

  const errors = useMemo(() => validateTagDraft(draft, existing, tag?.id), [draft, existing, tag?.id])
  const shown = attempted ? errors : {}
  const token = normalizeTagToken(draft.tag)

  const save = useMutation({
    mutationFn: () => {
      const request = tagRequestFromDraft(draft)
      return tag ? narrationApi.tags.update(tag.id, request) : narrationApi.tags.create(request)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: narrationTagsKey })
      onClose()
    },
  })

  const submit = () => {
    setAttempted(true)
    if (Object.keys(errors).length > 0) return
    save.mutate()
  }

  const set = <K extends keyof TagDraft>(key: K, value: TagDraft[K]) => setDraft((d) => ({ ...d, [key]: value }))
  const text = (key: 'tag' | 'label' | 'description' | 'sortOrder') => ({
    value: draft[key],
    onChange: (e: ChangeEvent<HTMLInputElement>) => set(key, e.target.value),
    error: Boolean(shown[key]),
    size: 'small' as const,
    fullWidth: true,
  })

  return (
    <Dialog open onClose={save.isPending ? undefined : onClose} maxWidth="xs" fullWidth {...dialogProps}>
      <DialogTitle>{creating ? 'New emotive tag' : `Edit [${tag.tag}]`}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2.5}>
          <TextField
            label="Tag"
            {...text('tag')}
            required
            inputProps={{ spellCheck: false, autoCapitalize: 'off', style: { fontFamily: 'monospace' } }}
            InputProps={{
              startAdornment: <InputAdornment position="start">[</InputAdornment>,
              endAdornment: <InputAdornment position="end">]</InputAdornment>,
            }}
            helperText={
              shown.tag ??
              (token ? `Creators write [${token}]. Lower-cased, brackets stripped.` : 'Lower-cased, brackets stripped.')
            }
          />
          <TextField
            label="Label"
            {...text('label')}
            placeholder={token ? token[0].toUpperCase() + token.slice(1) : undefined}
            InputLabelProps={{ shrink: true }}
            helperText={shown.label ?? 'Shown on the tag button. Blank uses the tag.'}
          />
          <TextField
            select
            label="Category"
            size="small"
            fullWidth
            value={categories.some((c) => c.id === draft.categoryId) ? draft.categoryId : ''}
            onChange={(e) => set('categoryId', e.target.value)}
            helperText="The palette tab creators find it under. None puts it under Other."
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {categories.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
                {c.isActive ? '' : ' (inactive)'}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description"
            {...text('description')}
            multiline
            minRows={2}
            helperText={shown.description ?? 'What it does to the reading, shown as a hint.'}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
            <Box>
              <FormControlLabel
                control={<Switch checked={draft.isActive} onChange={(e) => set('isActive', e.target.checked)} />}
                label="Active"
              />
              <Typography variant="caption" color="text.secondary" component="div">
                Inactive tags are refused in new generations.
              </Typography>
            </Box>
            <TextField
              label="Sort order"
              {...text('sortOrder')}
              type="number"
              helperText={shown.sortOrder ?? 'Lower first.'}
            />
          </Box>

          {attempted && Object.keys(errors).length > 0 && (
            <Alert severity="warning">Fix the highlighted fields to save.</Alert>
          )}
          {save.error && <Alert severity="error">{narrationErrorMessage(save.error, 'The tag could not be saved')}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={save.isPending}>
          Cancel
        </Button>
        <Button variant="contained" onClick={submit} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : creating ? 'Add tag' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
